import React from "react";
import { VisitQuestionnaireAnswer } from "@pages/ecoa/visit/VisitQuestionnaire";
import {
  CrfCheckItemSpec,
  CrfDescriptionItemSpec,
  CrfItemDisabilitySpec,
  CrfSingleSelectItemSpec,
  CrfTextItemSpec,
  EdcGroupId,
  InitialCrfPageSequence,
  InitialItemSequence,
  StudySpec,
} from "maven-lib/dist/consts/edc";
import { isDisableFor } from "maven-lib/dist/consts/edc/lib";
import { GetEcoaCapturesResponse } from "maven-lib/dist/dto/ecoaCapture";
import { EcoaQuestionnaire } from "maven-lib/dist/dto/entity/ecoaVisit";
import { classNames } from "maven-lib/dist/utils/misc";
import { QuestionnaireAccountNumberItem } from "./items/QuestionnaireAccountNumberItem";
import { QuestionnaireAutoTextItem } from "./items/QuestionnaireAutoTextItem";
import { QuestionnaireCheckItem } from "./items/QuestionnaireCheckItem";
import { QuestionnaireConstantItem } from "./items/QuestionnaireConstantItem";
import { QuestionnaireDateItem } from "./items/QuestionnaireDateItem";
import { QuestionnaireDescriptionItem } from "./items/QuestionnaireDescriptionItem";
import { QuestionnaireDropdownSelectItem } from "./items/QuestionnaireDropdownSelectItem";
import { QuestionnaireHorizontalSelectItem } from "./items/QuestionnaireHorizontalSelectItem";
import { QuestionnaireRadioItem } from "./items/QuestionnaireRadioItem";
import { QuestionnaireSliderItem } from "./items/QuestionnaireSliderItem";
import { QuestionnaireTextItem } from "./items/QuestionnaireTextItem";
import { QuestionnaireTimeItem } from "./items/QuestionnaireTimeItem";
import { uniqWith } from "lodash";

interface VisitQuestionnaireItemProps {
  disabled?: boolean;
  isAvailable?: boolean;
  studySpec?: StudySpec;
  questionnaire: EcoaQuestionnaire;
  folderId?: string;
  visitId?: string;
  visitSeq?: number;
  crfPageId?: string;
  crfPageSeq?: number;
  defaultItemValues?: Array<{ itemId: string | null; dbVal: string | number | null }>;
  capturedItems?: Array<{ itemId: string | null; dbVal: string | number | null }>;
  captureResponse?: GetEcoaCapturesResponse | null;
  onValueChange: (values: Array<VisitQuestionnaireAnswer>) => void;
  onChangeDisabledFooter: (val: boolean) => void;
  flattenQuestionnairesCurrentItemValues?: Array<{ itemId: string | null; dbVal: string | number | null }>;
}

export function VisitQuestionnaireItem({
  disabled,
  isAvailable,
  questionnaire,
  studySpec,
  folderId,
  visitId,
  visitSeq,
  crfPageId,
  crfPageSeq,
  defaultItemValues,
  capturedItems,
  onValueChange,
  onChangeDisabledFooter,
  captureResponse,
  flattenQuestionnairesCurrentItemValues,
}: VisitQuestionnaireItemProps) {
  const isShowQuestionnaireHorizontalSelectItem =
    questionnaire.item.type === "SINGLE_SELECT" &&
    questionnaire.item.original.type === "SINGLE_SELECT" &&
    questionnaire.item.original.layout === "RADIO" &&
    questionnaire.item.original.appearance === "ENUM";

  const isShowQuestionnaireRadioItem =
    questionnaire.item.type === "SINGLE_SELECT" &&
    questionnaire.item.original.type === "SINGLE_SELECT" &&
    questionnaire.item.original.layout === "RADIO" &&
    questionnaire.item.original.appearance !== "ENUM";

  const isShowQuestionnaireDropdownSelectItem =
    questionnaire.item.type === "SINGLE_SELECT" &&
    questionnaire.item.original.type === "SINGLE_SELECT" &&
    questionnaire.item.original.layout === "DROPDOWN";

  const isShowQuestionnaireTextItem =
    questionnaire.item.type === "TEXT" &&
    questionnaire.item.original.type === "TEXT" &&
    questionnaire.item.original.extra?.type !== "ACCOUNT_NUMBER";

  const isShowQuestionnaireAccountNumberItem =
    questionnaire.item.type === "TEXT" &&
    questionnaire.item.original.type === "TEXT" &&
    questionnaire.item.original.extra?.type === "ACCOUNT_NUMBER";

  const isShowQuestionnaireCheckItem =
    (questionnaire.item.type === "CHECK_GROUP" || questionnaire.item.type === "CHECK") && questionnaire.item.original.type === "CHECK";

  const childProps = {
    disabled,
    defaultItemValues,
    questionnaire,
    onValueChange,
    isAvailable,
  };

  const handleChangeTime = (values: Array<VisitQuestionnaireAnswer>) => {
    onValueChange(values);
  };

  const handleChangeCheck = (values: Array<VisitQuestionnaireAnswer>) => {
    onValueChange(values);
  };

  const isDisableItem = (itemDisability?: CrfItemDisabilitySpec | CrfItemDisabilitySpec[]) => {
    // NOTE: 20231013 PreScreening 컴포넌트에서는 folderId ~ crfPageSeq, studySpec을 넘겨주고 있지 않음. 만약 PreScreening쪽에서도 disable 속성을 지원해야 한다면 해당 props를 넘겨줘야 함
    if (!folderId || !visitId || !visitSeq || !crfPageId || !crfPageSeq || !studySpec) {
      return true;
    }

    const mergedCaptured =
      (captureResponse &&
        uniqWith(
          [...captureResponse.captured.temp, ...captureResponse.captured.items],
          (a, b) =>
            a.groupId === b.groupId &&
            a.folderId === b.folderId &&
            a.visitId === b.visitId &&
            a.visitSeq === b.visitSeq &&
            a.crfPageId === b.crfPageId &&
            a.crfPageSeq === b.crfPageSeq &&
            a.itemId === b.itemId &&
            a.itemSeq === b.itemSeq
        )) ||
      [];

    const tempCaptureResponse = captureResponse?.captured.temp || [];
    const mappedDefaultItemValues =
      flattenQuestionnairesCurrentItemValues
        ?.filter(({ dbVal }) => !!dbVal)
        .map(({ itemId, dbVal }) => ({
          itemId: itemId || "",
          dbVal,
          sectionId: questionnaire.item.sectionId,
          itemSeq: questionnaire.item.original.itemSeq || InitialItemSequence,
        }))
        .map(({ sectionId, itemId, itemSeq, dbVal }) => ({
          id: BigInt(0),
          groupId: EdcGroupId,
          folderId,
          visitId,
          visitSeq,
          crfPageId,
          crfPageSeq: crfPageSeq || InitialCrfPageSequence,
          sectionId,
          itemId,
          itemSeq,
          dbVal,
          calcVal: null,
          freshman: false,
          deleted: false,
        })) || [];

    const pendingItems = uniqWith(
      [...tempCaptureResponse, ...mappedDefaultItemValues],
      (a, b) =>
        a.groupId === b.groupId &&
        a.folderId === b.folderId &&
        a.visitId === b.visitId &&
        a.visitSeq === b.visitSeq &&
        a.crfPageId === b.crfPageId &&
        a.crfPageSeq === b.crfPageSeq &&
        a.itemId === b.itemId &&
        a.itemSeq === b.itemSeq
    );

    return (
      !!itemDisability &&
      !!capturedItems?.length &&
      isDisableFor(studySpec, mergedCaptured, pendingItems, itemDisability, {
        groupId: EdcGroupId,
        folderId: folderId,
        visitId: visitId,
        visitSeq: visitSeq,
        crfPageId: crfPageId,
        crfPageSeq: crfPageSeq,
      })
    );
  };

  return (
    <>
      {questionnaire.item.type === "DESCRIPTION" && (
        <QuestionnaireDescriptionItem item={questionnaire.item.original as CrfDescriptionItemSpec} />
      )}
      {isShowQuestionnaireHorizontalSelectItem && (
        <QuestionnaireHorizontalSelectItem {...childProps} item={questionnaire.item.original as CrfSingleSelectItemSpec} />
      )}
      {isShowQuestionnaireRadioItem && (
        <QuestionnaireRadioItem {...childProps} item={questionnaire.item.original as CrfSingleSelectItemSpec} />
      )}
      {isShowQuestionnaireDropdownSelectItem && (
        <QuestionnaireDropdownSelectItem {...childProps} item={questionnaire.item.original as CrfSingleSelectItemSpec} />
      )}
      {isShowQuestionnaireCheckItem && (
        <QuestionnaireCheckItem
          {...childProps}
          folderId={folderId}
          visitId={visitId}
          visitSeq={visitSeq}
          crfPageId={crfPageId}
          crfPageSeq={crfPageSeq}
          studySpec={studySpec}
          item={questionnaire.item.original as CrfCheckItemSpec}
        />
      )}
      {isShowQuestionnaireTextItem && <QuestionnaireTextItem {...childProps} item={questionnaire.item.original as CrfTextItemSpec} />}
      {isShowQuestionnaireAccountNumberItem && (
        <QuestionnaireAccountNumberItem
          {...childProps}
          item={questionnaire.item.original as CrfTextItemSpec}
          capturedItems={capturedItems}
          onChangeDisabledFooter={onChangeDisabledFooter}
        />
      )}
      {questionnaire.item.type === "SLIDER" && questionnaire.item.original.type === "SLIDER" && (
        <QuestionnaireSliderItem {...childProps} item={questionnaire.item.original} />
      )}
      {questionnaire.item.type === "DATE" && questionnaire.item.original.type === "DATE" && (
        <QuestionnaireDateItem {...childProps} item={questionnaire.item.original} />
      )}
      {questionnaire.item.type === "TIME" && questionnaire.item.original.type === "TIME" && (
        <QuestionnaireTimeItem
          {...childProps}
          item={questionnaire.item.original}
          prefix={questionnaire.item.original.uiPrefix}
          suffix={questionnaire.item.original.uiSuffix}
        />
      )}
      {questionnaire.item.type === "CONSTANT" && questionnaire.item.original.type === "CONSTANT" && (
        <QuestionnaireConstantItem questionnaire={questionnaire} item={questionnaire.item.original} />
      )}
      {questionnaire.item.type === "AUTO_TEXT" && questionnaire.item.original.type === "AUTO_TEXT" && (
        <QuestionnaireAutoTextItem
          {...childProps}
          folderId={folderId}
          visitId={visitId}
          visitSeq={visitSeq}
          crfPageId={crfPageId}
          crfPageSeq={crfPageSeq}
          item={questionnaire.item.original}
        />
      )}
      {questionnaire.item.original.children?.items.map((childrenItem, childrenItemIndex) => {
        const isDisabled = isDisableItem(childrenItem?.disability);

        if (childrenItem?.type === "TIME") {
          return (
            <>
              <div className={classNames("app-ecoa-children-time-wrapper", disabled && "--disabled")} key={childrenItemIndex}>
                <div style={{ width: "70%", marginLeft: "40px" }}>
                  <QuestionnaireTimeItem
                    item={childrenItem}
                    disabled={disabled || isDisabled}
                    isAvailable={isAvailable}
                    defaultItemValues={defaultItemValues}
                    onValueChange={handleChangeTime}
                    prefix={childrenItem.uiPrefix}
                    suffix={childrenItem.uiSuffix}
                  />
                </div>
              </div>
            </>
          );
        } else if (childrenItem?.type === "CHECK") {
          return (
            <div className="hb-check-wrapper" key={childrenItemIndex}>
              <QuestionnaireCheckItem
                {...childProps}
                folderId={folderId}
                visitId={visitId}
                visitSeq={visitSeq}
                crfPageId={crfPageId}
                crfPageSeq={crfPageSeq}
                studySpec={studySpec}
                item={childrenItem}
                disabled={disabled || isDisabled}
                onValueChange={handleChangeCheck}
              />
            </div>
          );
        } else {
          return <></>;
        }
      })}
    </>
  );
}
